import React, { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "config/apiConfig";

// CSS imports [제거 금지]
import 'styles/Font.css';

// Image imports [제거 금지]
import arrowRightIcon from "assets/images/icon/arrow-right-icon.svg";
// [Fallback 이미지용]
import defaultBranchImage from "assets/images/branch/wiryeJungangBranch.svg";

// Component imports [제거 금지]
import TopNavigator from "components/layout/TopNavigator/TopNavigator";
import ConfirmModal from "components/ui/Modals/ConfirmModal/ConfirmModal";
import Footer from "components/layout/Footer/Footer";
import PaymentPeriodSelector from "pages/MyPage/components/PaymentPeriodSelector";
import PaymentPeriodBottomSheet from "pages/MyPage/components/PaymentPeriodBottomSheet";
import PaymentDetailModal from "components/ui/Modals/PaymentDetailModal/PaymentDetailModal";

// Utils
import { getTokenStatus } from 'utils/tokenManager';
// import usePreventZoom from "hooks/usePreventZoom";
import usePreventInputScroll from "hooks/usePreventInputScroll";
import usePreventHorizontalScroll from "hooks/usePreventHorizontalScroll";


// **********************************************
// * 1. 유틸리티 함수
// **********************************************
const formatPrice = (price) => {
    if (!price) return '0원';
    const numberPrice = Number(String(price).replace(/[^0-9]/g, ''));
    return `${numberPrice.toLocaleString()}원`;
};

const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const cleaned = dateStr.replace(/[^0-9]/g, '');
    if (cleaned.length === 8) {
        return `${cleaned.substring(0, 4)}.${cleaned.substring(4, 6)}.${cleaned.substring(6, 8)}`;
    }
    return dateStr;
};

// 구독 유효성 체크 (오늘 날짜가 종료일 포함 이전인지)
const isSubscriptionActive = (endDateStr) => {
    if (!endDateStr) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const cleaned = endDateStr.replace(/\./g, '');
    if (cleaned.length !== 8) return false;

    const year = parseInt(cleaned.substring(0, 4));
    const month = parseInt(cleaned.substring(4, 6)) - 1;
    const day = parseInt(cleaned.substring(6, 8));

    const end = new Date(year, month, day);
    end.setHours(0, 0, 0, 0);

    return end >= today;
};

const sortPaymentsByDate = (data) => {
    return [...data].sort((a, b) => b.date.localeCompare(a.date));
};

const getDateMonthsAgo = (date, months) => {
    const d = new Date(date);
    d.setMonth(d.getMonth() - months);
    if (d.getDate() !== date.getDate()) {
        d.setDate(0);
    }
    return d;
};


// **********************************************
// * 2. PaymentCard 컴포넌트
// **********************************************
const PaymentCard = ({ data, onClick, onCancelClick }) => {
    // 구독 상품 판단: auto 값이 'Y' 또는 'N'이면 구독 상품으로 간주
    const isSubscriptionProduct = data.auto === 'Y' || data.auto === 'N';

    // 자동 결제 활성화 여부
    const isAutoActive = data.auto === 'Y';

    // 기간 유효 여부 (기간이 남았는지)
    const isActivePeriod = isSubscriptionActive(data.edate);

    // 기간 데이터 존재 여부
    const showPeriod = data.sdate && data.edate;

    return (
        <div
            className="paymentProductDiv"
            style={{ ...staticStyles.paymentProductCard, cursor: 'pointer' }}
            onClick={() => onClick(data)}
        >
            <div className="patmentDateDetailDiv" style={staticStyles.patmentDateDetailDiv}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <span className="paymentDateDetail" style={staticStyles.paymentDateDetailText}>
                        결제일: {data.date}
                    </span>
                    {/* 기간 데이터가 있으면 표시 */}
                    {showPeriod && (
                        <span style={{ ...staticStyles.paymentDateDetailText, fontSize: '13px', color: '#9A9A9A' }}>
                            이용기간: {data.sdate} ~ {data.edate}
                        </span>
                    )}
                </div>

                {/* [버튼] 구독 상품이고 + 자동결제 중('Y')이며 + 기간이 남았을 때만 '취소' 버튼 */}
                {isSubscriptionProduct && isAutoActive && isActivePeriod ? (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onCancelClick(data);
                        }}
                        style={staticStyles.cancelButton}
                    >
                        구독 취소
                    </button>
                ) : (
                    <div className="arrowDiv" style={staticStyles.arrowDiv}>
                        <img src={arrowRightIcon} alt="arrow" style={staticStyles.arrowIcon} />
                    </div>
                )}
            </div>

            <div className="horizonBar" style={staticStyles.horizonBar} />

            <div className="productInfoDiv" style={staticStyles.productInfoDiv}>
                <img
                    alt="지점 사진"
                    src={data.imageSrc || defaultBranchImage}
                    style={staticStyles.branchImage}
                    onError={(e) => {
                        e.target.src = defaultBranchImage
                    }}
                />

                <div className="productInfo" style={staticStyles.productInfo}>
                    <div className="branchNameDiv">
                        <span className="branchName" style={staticStyles.branchName}>
                            {data.branchName}
                        </span>
                    </div>
                    <div className="productNameDiv">
                        <span className="productName" style={staticStyles.productName}>
                            {data.productName}
                        </span>
                    </div>
                </div>

                <div className="paymentPriceDiv" style={staticStyles.paymentPriceDiv}>
                    <span className="paymentPrice" style={staticStyles.paymentPrice}>
                        {data.price}
                    </span>
                </div>
            </div>
        </div>
    );
};


// **********************************************
// * 3. PaymentHistory 메인 컴포넌트
// **********************************************
function PaymentHistory() {
    useNavigate();
    // usePreventZoom();
    usePreventInputScroll();
    usePreventHorizontalScroll();

    const TODAY = new Date();
    const navigate = useNavigate();

    // Refs
    const mainContentRef = useRef(null);
    const headerRef = useRef(null);

    // UI State
    const [headerHeight, setHeaderHeight] = useState(0);

    // Filter & Pagination State
    const initialMonths = 3;
    const [monthsToShow, setMonthsToShow] = useState(initialMonths);
    const [visibleGeneralCount, setVisibleGeneralCount] = useState(3);

    // 탭 상태 관리 ('ACTIVE': 이용 중, 'EXPIRED': 이용 완료)
    const [activeTab, setActiveTab] = useState('ACTIVE');

    // Data State
    const [paymentData, setPaymentData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    // Modal State
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [itemToCancel, setItemToCancel] = useState(null);
    const [isPeriodModalOpen, setIsPeriodModalOpen] = useState(false);

    // 상세 모달 State
    const [selectedDetailItem, setSelectedDetailItem] = useState(null);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

    // 결과 알림용 모달 상태 (성공/실패)
    const [resultModal, setResultModal] = useState({
        isOpen: false,
        title: '',
        message: ''
    });

    // API: 데이터 로드
    useEffect(() => {
        const fetchOrderHistory = async () => {
            const { token } = getTokenStatus();
            const REQUEST_API_URL = `${API_BASE_URL}/myorder`;

            try {
                const response = await fetch(REQUEST_API_URL, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                        'Authorization': `Bearer ${token}`,
                    },
                });

                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

                const result = await response.json();
                const list = result.data?.list || [];

                const mappedData = list.map((item, index) => ({
                    id: item.odKey || `order_${index}`,
                    date: formatDate(item.date),
                    sdate: formatDate(item.sdate),
                    edate: formatDate(item.edate),
                    branchName: item.brName,
                    productName: item.name,
                    price: formatPrice(item.price),
                    imageSrc: item.brImage,
                    auto: item.auto,
                }));

                setPaymentData(sortPaymentsByDate(mappedData));

            } catch (error) {
                console.error("결제 내역 불러오기 실패:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchOrderHistory();
    }, [navigate]);


    // 동적 헤더 높이 측정
    useEffect(() => {
        const measureHeight = () => {
            if (headerRef.current) {
                setHeaderHeight(headerRef.current.offsetHeight);
            }
        };
        measureHeight();
        let animationFrame;
        const debouncedMeasure = () => {
            if (animationFrame) cancelAnimationFrame(animationFrame);
            animationFrame = requestAnimationFrame(measureHeight);
        };
        window.addEventListener('resize', debouncedMeasure);
        return () => {
            window.removeEventListener('resize', debouncedMeasure);
            if (animationFrame) cancelAnimationFrame(animationFrame);
        };
    }, []);

    // [데이터 분류 로직 변경]
    const { subscriptions, manualActive, manualExpired } = useMemo(() => {
        // 이용 중인 정기 구독 상품 (Top Section)
        // 조건: auto === 'Y' (만료 여부 상관없이 구독 설정된 상품)
        const subs = paymentData.filter(item => item.auto === 'Y');

        // 그 외 (auto === 'N' 등)
        const others = paymentData.filter(item => item.auto !== 'Y');

        // 탭 분류를 위한 필터링
        // Tab 1: 이용 중 (Active) -> 기간이 유효함
        const mActive = others.filter(item => isSubscriptionActive(item.edate));

        // Tab 2: 이용 완료 (Expired) -> 기간이 지남
        const mExpiredCandidates = others.filter(item => !isSubscriptionActive(item.edate));

        // 종료된 상품은 결제일 필터(monthsToShow) 적용
        let mExpired = mExpiredCandidates;
        if (monthsToShow !== Infinity) {
            const cutoffDate = getDateMonthsAgo(TODAY, monthsToShow);
            mExpired = mExpiredCandidates.filter(item => {
                const [year, month, day] = item.date.split('.').map(Number);
                const paymentDate = new Date(year, month - 1, day);
                return paymentDate >= cutoffDate;
            });
        }

        return {
            subscriptions: subs,
            manualActive: mActive,
            manualExpired: mExpired
        };
    }, [paymentData, monthsToShow]);

    // '종료된 상품' 리스트에만 더보기(Pagination) 적용
    const displayedExpired = manualExpired.slice(0, visibleGeneralCount);
    const isMoreExpiredAvailable = displayedExpired.length < manualExpired.length;


    // 더보기 버튼
    const handleLoadMore = () => {
        setVisibleGeneralCount(prev => prev + 5);
    };

    const handlePeriodApply = ({ newMonthsToShow }) => {
        if (newMonthsToShow) setMonthsToShow(newMonthsToShow);
        setIsPeriodModalOpen(false);
        setVisibleGeneralCount(3);
    };

    // 기간 텍스트
    let displayPeriod;
    const oldestItem = manualExpired.length > 0 ? manualExpired[manualExpired.length - 1] : null;
    if (oldestItem) {
        const [startYear, startMonth] = oldestItem.date.split('.').map(Number);
        displayPeriod = `${startYear}년 ${startMonth}월 ~ ${TODAY.getFullYear()}년 ${TODAY.getMonth() + 1}월`;
    } else {
        displayPeriod = `${TODAY.getFullYear()}년 ${TODAY.getMonth() + 1}월 ~ ${TODAY.getFullYear()}년 ${TODAY.getMonth() + 1}월`;
    }

    // --- 핸들러 ---
    const handleTabChange = (tab) => {
        setActiveTab(tab);
        // 탭 변경 시 더보기 초기화
        setVisibleGeneralCount(3);
    };

    const handleCardClick = (item) => {
        setSelectedDetailItem(item);
        setIsDetailModalOpen(true);
    };

    const handleCancelClick = (item) => {
        setIsDetailModalOpen(false);
        setItemToCancel(item);
        setIsConfirmModalOpen(true);
    };

    const handleConfirmClose = () => {
        setIsConfirmModalOpen(false);
        setItemToCancel(null);
        setIsPeriodModalOpen(false);
    };

    const handlePeriodSelectorClick = () => {
        setIsPeriodModalOpen(true);
    };

    // 구독 취소 확정 로직
    const handleConfirmAction = async () => {
        if (itemToCancel) {
            const { token } = getTokenStatus();
            if (!token) return;

            try {
                const response = await fetch(`${API_BASE_URL}/delAuto`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        odKey: itemToCancel.id
                    })
                });

                const data = await response.json();

                if (response.ok && (data.code === 200 || data.code === '200' || data.status === 'success')) {
                    setResultModal({
                        isOpen: true,
                        title: '구독 취소 완료',
                        message: `[${itemToCancel.productName}] 구독이\n정상적으로 취소되었습니다.`
                    });

                    // UI 상태 업데이트: auto='N'으로 변경
                    setPaymentData(prevData =>
                        prevData.map(item =>
                            item.id === itemToCancel.id
                                ? { ...item, auto: 'N' }
                                : item
                        )
                    );
                } else {
                    const msg = data.massage || data.message || "구독 취소에 실패했습니다.";
                    setResultModal({
                        isOpen: true,
                        title: '구독 취소 실패',
                        message: msg
                    });
                }

            } catch (e) {
                console.error("Cancel Subscription Error:", e);
                setResultModal({
                    isOpen: true,
                    title: '오류',
                    message: "서버 통신 중 오류가 발생했습니다."
                });
            }
        }
        setIsConfirmModalOpen(false);
        setItemToCancel(null);
    };

    const closeResultModal = () => {
        setResultModal(prev => ({ ...prev, isOpen: false }));
    };

    const dynamicMainContentStyle = {
        ...staticStyles.mainContent,
        paddingTop: `${headerHeight}px`,
    };

    return (
        <>
            <div className="backgroundWrapper" style={staticStyles.backgroundWrapper}>
                <div style={staticStyles.fixedHeaderWrapper} ref={headerRef}>
                    <TopNavigator title="결제 내역" />

                    {/* 기간 선택기는 '종료된 상품' 탭에서만 유의미하므로 조건부 표시 또는 항상 표시 */}
                    <div className="paymentDateDiv" style={staticStyles.paymentDateDiv}>
                        <PaymentPeriodSelector
                            periodText={displayPeriod}
                            onSelectorClick={handlePeriodSelectorClick}
                            disabled={activeTab === 'ACTIVE'} // 이용 중 탭에서는 비활성화 (선택 사항)
                        />
                    </div>
                </div>

                <main ref={mainContentRef} style={dynamicMainContentStyle}>
                    {isLoading ? (
                        <div style={{ color: 'white', marginTop: '50px' }}>내역을 불러오는 중...</div>
                    ) : (
                        <>
                            {/* 1. 정기 구독 상품 (Top Section, auto='Y') */}
                            {subscriptions.length > 0 && (
                                <div style={{ width: '100%', marginBottom: '24px' }}>
                                    <p style={staticStyles.sectionTitle}>이용 중인 구독 상품</p>
                                    <div style={staticStyles.cardList}>
                                        {subscriptions.map((data) => (
                                            <PaymentCard
                                                key={data.id}
                                                data={data}
                                                onClick={handleCardClick}
                                                onCancelClick={handleCancelClick}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* 2. 일반 상품 탭 (auto='N') */}
                            <div style={{ width: '100%' }}>
                                {/* 탭 버튼 */}
                                <div style={staticStyles.tabContainer}>
                                    <button
                                        style={activeTab === 'ACTIVE' ? staticStyles.tabButtonActive : staticStyles.tabButtonInactive}
                                        onClick={() => handleTabChange('ACTIVE')}
                                    >
                                        이용 중
                                    </button>
                                    <button
                                        style={activeTab === 'EXPIRED' ? staticStyles.tabButtonActive : staticStyles.tabButtonInactive}
                                        onClick={() => handleTabChange('EXPIRED')}
                                    >
                                        종료된 상품
                                    </button>
                                </div>

                                {/* 탭 컨텐츠 */}
                                <div style={staticStyles.cardList}>
                                    {activeTab === 'ACTIVE' ? (
                                        // 이용 중 (Active) 리스트
                                        manualActive.length > 0 ? (
                                            manualActive.map((data) => (
                                                <PaymentCard
                                                    key={data.id}
                                                    data={data}
                                                    onClick={handleCardClick}
                                                    onCancelClick={handleCancelClick}
                                                />
                                            ))
                                        ) : (
                                            <div style={staticStyles.emptyMessage}>이용 중인 상품이 없습니다.</div>
                                        )
                                    ) : (
                                        // 종료된 상품 (Expired) 리스트
                                        displayedExpired.length > 0 ? (
                                            displayedExpired.map((data) => (
                                                <PaymentCard
                                                    key={data.id}
                                                    data={data}
                                                    onClick={handleCardClick}
                                                    onCancelClick={handleCancelClick}
                                                />
                                            ))
                                        ) : (
                                            <div style={staticStyles.emptyMessage}>종료된 상품이 없습니다.</div>
                                        )
                                    )}
                                </div>
                            </div>
                        </>
                    )}

                    {/* 더보기 버튼 (종료된 상품 탭에서만 표시) */}
                    {!isLoading && activeTab === 'EXPIRED' && isMoreExpiredAvailable && (
                        <button
                            className="TransferHistoryButton"
                            style={staticStyles.transferHistoryButton}
                            onClick={handleLoadMore}
                        >
                            <div className="TransferHistoryDiv" style={staticStyles.transferHistoryDiv}>
                                <span className="TransferHistoryText" style={staticStyles.transferHistoryText}>
                                    이전 내역 보기
                                </span>
                                <div className="arrowDiv" style={staticStyles.transferHistoryArrowDiv}>
                                    <img
                                        className="arrowIconDown"
                                        src={arrowRightIcon}
                                        alt="arrow"
                                        style={staticStyles.transferHistoryArrowIcon}
                                    />
                                </div>
                            </div>
                        </button>
                    )}

                    <Footer />
                </main>

                <ConfirmModal
                    isOpen={isConfirmModalOpen}
                    onClose={handleConfirmClose}
                    onConfirm={handleConfirmAction}
                    title="구독 취소 확인"
                    message={itemToCancel ? `선택하신\n${itemToCancel.branchName}의 ${itemToCancel.productName} 구독을\n취소하시겠습니까?` : ""}
                />

                <ConfirmModal
                    isOpen={resultModal.isOpen}
                    onClose={closeResultModal}
                    onConfirm={closeResultModal}
                    title={resultModal.title}
                    message={resultModal.message}
                    showCancel={false}
                />

                <PaymentPeriodBottomSheet
                    isOpen={isPeriodModalOpen}
                    onClose={handleConfirmClose}
                    onApply={handlePeriodApply}
                    currentMonthsToShow={monthsToShow}
                />

                <PaymentDetailModal
                    isOpen={isDetailModalOpen}
                    onClose={() => setIsDetailModalOpen(false)}
                    data={selectedDetailItem}
                    onCancelSubscription={handleCancelClick}
                />
            </div>
        </>
    );
}

// [staticStyles]
const staticStyles = {
    backgroundWrapper: {
        background: '#0D042D', width: '100%', height: '100dvh',
        display: 'flex', flexDirection: 'column', alignItems: 'stretch',
        margin: '0 auto', boxSizing: 'border-box', position: 'relative',
    },
    statusBar: { flexShrink: 0, backgroundColor: 'transparent' },
    fixedHeaderWrapper: {
        position: 'fixed', top: 0, left: 0, width: '100%', zIndex: 100,
        backgroundColor: '#0D042D', display: 'flex', flexDirection: 'column',
    },
    mainContent: {
        flex: 1, overflowY: 'auto', padding: '0 24px 24px',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '19px',
    },
    paymentDateDiv: {
        width: '100%', marginTop: '12px', padding: '0 24px 10px', boxSizing: 'border-box',
    },
    sectionTitle: {
        fontFamily: 'Pretendard', fontWeight: 'bold', fontSize: '18px', color: '#FFFFFF',
        marginBottom: '12px', textAlign: 'left', width: '100%'
    },
    // 탭 스타일
    tabContainer: {
        display: 'flex', width: '100%', borderBottom: '1px solid #2C2A40',
        marginBottom: '16px',
    },
    tabButtonActive: {
        flex: 1, height: '40px', background: 'transparent', border: 'none',
        borderBottom: '2px solid #8052FF', color: '#FFFFFF',
        fontFamily: 'Pretendard', fontWeight: '700', fontSize: '15px', cursor: 'pointer',
    },
    tabButtonInactive: {
        flex: 1, height: '40px', background: 'transparent', border: 'none',
        borderBottom: '2px solid transparent', color: '#747474',
        fontFamily: 'Pretendard', fontWeight: '500', fontSize: '15px', cursor: 'pointer',
    },
    emptyMessage: {
        color: '#9A9A9A', textAlign: 'center', padding: '40px 0', width: '100%',
    },
    // [기존 스타일]
    cardList: {
        display: 'flex', flexDirection: 'column', gap: '16px', width: '100%'
    },
    paymentProductCard: {
        width: '100%', height: '186px', borderRadius: '10px', background: '#1B1931',
        padding: '18px 24px', boxSizing: 'border-box',
        display: 'flex', flexDirection: 'column', justifyContent: 'space-between', flexShrink: 0,
    },
    patmentDateDetailDiv: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        marginBottom: '10px', flexShrink: 0,
    },
    paymentDateDetailText: {
        fontFamily: 'Pretendard', fontWeight: 500, fontSize: '16px',
        lineHeight: '17px', color: '#FFFFFF',
    },
    arrowDiv: {
        width: '24px', height: '24px', display: 'flex', justifyContent: 'center',
        alignItems: 'center', cursor: 'pointer', flexShrink: 0,
    },
    arrowIcon: { width: '7px', height: '14px' },
    horizonBar: {
        width: '100%', height: '1px', background: '#3A394A', marginBottom: '15px', flexShrink: 0,
    },
    productInfoDiv: {
        display: 'flex', alignItems: 'flex-start', width: '100%', height: '88px', position: 'relative',
    },
    branchImage: {
        width: '88px', height: '88px', borderRadius: '8px', flexShrink: 0, objectFit: 'cover',
    },
    productInfo: {
        display: 'flex', flexDirection: 'column', marginLeft: '24px', flex: 1,
    },
    branchName: {
        fontFamily: 'Pretendard', fontWeight: 400, fontSize: '14px', lineHeight: '28px', color: '#9A9A9A', margin: 0,
    },
    productName: {
        fontFamily: 'Pretendard', fontWeight: 600, fontSize: '16px', lineHeight: '28px', color: '#FCFCFA', margin: 0,
    },
    paymentPriceDiv: {
        position: 'absolute', left: '112px', bottom: 0, height: '28px',
    },
    paymentPrice: {
        fontFamily: 'Pretendard',
        fontWeight: 600,
        fontSize: '18px',
        lineHeight: '28px',
        color: '#FCFCFA',
        margin: 0,
        whiteSpace: 'nowrap',
    },
    cancelButton: {
        padding: '4px 10px',
        backgroundColor: 'transparent',
        border: '1px solid #FF5252',
        borderRadius: '4px',
        color: '#FF5252',
        fontSize: '12px',
        fontWeight: 'bold',
        cursor: 'pointer',
        fontFamily: 'Pretendard'
    },
    transferHistoryButton: {
        width: '100%', height: '56px', borderRadius: '8px', border: '1px solid #9A9A9A',
        background: '#0D042D', cursor: 'pointer', display: 'flex', justifyContent: 'center',
        alignItems: 'center', padding: 0, boxSizing: 'border-box', flexShrink: 0,
    },
    transferHistoryDiv: {
        display: 'flex', alignItems: 'center', gap: '8px', height: '24px',
    },
    transferHistoryText: {
        fontFamily: 'Pretendard',
        fontWeight: 400,
        fontSize: '16px',
        lineHeight: '21px',
        color: '#9A9A9A',
        margin: 0,
        whiteSpace: 'nowrap',
        display: 'flex',
        alignItems: 'center',
    },
    transferHistoryArrowDiv: {
        width: '24px', height: '24px', display: 'flex', justifyContent: 'center', alignItems: 'center', flexShrink: 0,
    },
    transferHistoryArrowIcon: { width: '50%', height: '50%', transform: 'rotate(90deg)' },
};

export default PaymentHistory;